import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';

/**
 * Get current user's affiliate status
 * GET /api/affiliate/me
 * Returns affiliate info if user is an affiliate, or { is_affiliate: false } if not
 */
export async function fetchAffiliateStatus() {
    const response = await fetchWithAuth(`${BASE_URL}/api/affiliate/me`);

    // 404 means user is not an affiliate yet
    if (response.status === 404) {
        return { is_affiliate: false, can_become_affiliate: true };
    }

    if (!response.ok) {
        logError('Failed to fetch affiliate status', response.status);
        throw new Error('Failed to fetch affiliate status');
    }
    return response.json();
}

/**
 * Sign up as an affiliate
 * POST /api/affiliate/signup
 * @param {Object} payload - { first_name, last_name, paypal_email? }
 */
export async function signupAsAffiliate(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/affiliate/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    if (response.status === 409) {
        const error = new Error('You are already registered as an affiliate');
        error.status = 409;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to signup as affiliate', response.status);
        const error = new Error('Failed to join affiliate program');
        error.status = response.status;
        throw error;
    }
    return response.json();
}

/**
 * Get referral link
 * GET /api/affiliate/link
 */
export async function fetchReferralLink() {
    const response = await fetchWithAuth(`${BASE_URL}/api/affiliate/link`);

    if (response.status === 404) {
        const error = new Error('You need to join the affiliate program first');
        error.status = 404;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to fetch referral link', response.status);
        throw new Error('Failed to fetch referral link');
    }
    return response.json();
}

/**
 * Get list of referrals
 * GET /api/affiliate/referrals
 * @param {Object} params - { status?, limit?, page? }
 */
export async function fetchReferrals(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.page) query.set('page', String(params.page));

    const queryString = query.toString();
    const url = `${BASE_URL}/api/affiliate/referrals${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);

    if (response.status === 404) {
        const error = new Error('You need to join the affiliate program first');
        error.status = 404;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to fetch referrals', response.status);
        throw new Error('Failed to fetch referrals');
    }
    return response.json();
}

/**
 * Get list of commissions
 * GET /api/affiliate/commissions
 * @param {Object} params - { status?, limit?, page? }
 */
export async function fetchCommissions(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.page) query.set('page', String(params.page));

    const queryString = query.toString();
    const url = `${BASE_URL}/api/affiliate/commissions${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);

    if (response.status === 404) {
        const error = new Error('You need to join the affiliate program first');
        error.status = 404;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to fetch commissions', response.status);
        throw new Error('Failed to fetch commissions');
    }
    return response.json();
}

/**
 * Get payout settings
 * GET /api/affiliate/payout-settings
 */
export async function fetchPayoutSettings() {
    const response = await fetchWithAuth(`${BASE_URL}/api/affiliate/payout-settings`);

    if (response.status === 404) {
        const error = new Error('You need to join the affiliate program first');
        error.status = 404;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to fetch payout settings', response.status);
        throw new Error('Failed to fetch payout settings');
    }
    return response.json();
}

/**
 * Get Stripe Connect OAuth URL
 * POST /api/affiliate/stripe-connect
 * Returns URL to redirect user to Stripe Connect onboarding
 */
export async function initiateStripeConnect() {
    const response = await fetchWithAuth(`${BASE_URL}/api/affiliate/stripe-connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });

    if (response.status === 404) {
        const error = new Error('You need to join the affiliate program first');
        error.status = 404;
        throw error;
    }

    if (!response.ok) {
        logError('Failed to initiate Stripe Connect', response.status);
        throw new Error('Failed to connect Stripe account');
    }
    return response.json();
}

/**
 * Get Rewardful referral ID from cookie (client-side)
 * Call this during signup to capture the referral
 */
export function getRewardfulReferralId() {
    if (typeof window !== 'undefined' && window.Rewardful) {
        return window.Rewardful.referral || null;
    }
    return null;
}
